import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  NavigationItem,
  navigationItemsWithPatient,
} from '../../models/navigation-item-model';
import { LabelService } from '../../services/label.service';
import { ActivatedRoute } from '@angular/router';
import { classes, pages } from '../../shared/labels';
import {
  LabelAndDescription,
  LifeLineEvent,
  PatientEssentialInfo,
} from '../../models/patient-model';
import { PatientService } from '../../services/patient.service';
import { AddEventLifeLine, EventLifeLine } from '../../models/life-line-models';
import { ErrorPanelService } from '../../services/error-panel.service';
import { SidebarService } from '../../services/sidebar.service';

@Component({
  selector: 'app-patient-life-line-page',
  standalone: true,
  imports: [SharedModule, CommonModule, FormsModule],
  templateUrl: './patient-life-line-page.component.html',
  styleUrl: './patient-life-line-page.component.scss',
})
export class PatientLifeLinePageComponent implements OnInit {
  navigationItems: NavigationItem[] = [];
  name: string = '';
  patientName: string = '';
  patientSurname: string = '';
  patientNameAndSurname: string = '';
  isPatientActive: boolean = false;
  editOrDeleteString: string = 'delete';
  isSignificantEventSelected: string = 'selected';
  isLifeLineSelected: string = 'not-selected';
  showNewSignificantEventPanel: boolean = false;
  showModifyEventPanel: boolean = false;

  patientId = '';
  lifeLineEvents: EventLifeLine[] = [];
  selectedEvent!: EventLifeLine;

  panelTitle: string = 'Modifica Evento';
  eventTitle: string = '';
  eventStartDay: string = '';
  eventStartMonth: string = '';
  eventStartYear: string = '';
  eventEndDay: string = '';
  eventEndMonth: string = '';
  eventEndYear: string = '';

  isLoadingEvents: boolean = true;

  hasModifyButtonBeenClicked: boolean = false;

  isMobile = window.innerWidth < 768;

  constructor(
    public labelService: LabelService,
    private route: ActivatedRoute,
    private sidebarService: SidebarService,
    private patientService: PatientService,
    public errorPanelService: ErrorPanelService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.patientService
        .getPatientEssentialInfo(params['patientId'])
        .subscribe({
          next: (data: PatientEssentialInfo) => {
            this.patientId = data.id;
            this.patientName = data.first_name;
            this.patientSurname = data.last_name;
            this.patientNameAndSurname = `${this.patientName} ${this.patientSurname}`;
            this.isPatientActive = data.is_active;
            this.navigationItems = this.sidebarService.updateNavigationItems(
              navigationItemsWithPatient,
              this.patientName,
              this.patientSurname,
              this.patientId,
              pages.lifeLine
            );
            this.fetchSignificantEvents();
          },
          error: (err) => {
            console.error('Error getting patient essential info:', err);
            this.errorPanelService.showErrorSnackBar(
              'Errore nel recuperare i dati del paziente'
            );
          },
        });
    });
  }

  fetchSignificantEvents() {
    this.isLoadingEvents = true;
    this.lifeLineEvents = [];
    this.patientService
      .getLifeLineEventsWithSubEvents(this.patientId)
      .subscribe({
        next: (response) => {
          this.lifeLineEvents = response;
          this.isLoadingEvents = false;
        },
        error: (err) => {
          console.error('Errore nel recuperare gli eventi significativi', err);
          this.errorPanelService.showErrorSnackBar(
            'Errore nel recuperare gli eventi significativi'
          );
        },
      });
  }

  parseDate(dateStr: string): Date {
    const [month, year] = dateStr.split('/');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date;
  }

  displaySignificantEventSection() {
    this.isSignificantEventSelected = 'selected';
    this.isLifeLineSelected = 'not-selected';
  }
  displayLifeLineSection() {
    this.isSignificantEventSelected = 'not-selected';
    this.isLifeLineSelected = 'selected';
  }

  confirmModifyEvent(event: LabelAndDescription[]) {
    // transform from array to a json
    var mergedObject = event.reduce<{ [key: string]: any }>((acc, current) => {
      acc[current.label] = current.description;
      return acc;
    }, {});

    mergedObject = {
      patient_id: this.patientId,
      ...mergedObject,
    };

    this.patientService
      .putLifeLineEvent(this.selectedEvent.id, mergedObject)
      .subscribe({
        next: (response) => {
          this.fetchSignificantEvents();
        },
        error: (error) => {
          console.error('Errore nel modificare un evento', error);
          this.errorPanelService.showErrorSnackBar(
            'Errore nel modificare un evento'
          );
        },
      });
  }

  deleteLifeLineEvent(event: LifeLineEvent) {
    this.patientService.deleteLifeLineEvent(event.id).subscribe({
      next: (response) => {
        this.fetchSignificantEvents();
      },
      error: (error) => {
        console.error("Errore nell'eliminare un evento", error);
        this.errorPanelService.showErrorSnackBar(
          "Errore nell'eliminare un evento"
        );
      },
    });
  }

  toggleNewSignificantEventPanel() {
    this.showNewSignificantEventPanel = !this.showNewSignificantEventPanel;
    this.panelTitle = 'Nuovo Evento Significativo';
    this.eventTitle = '';
    this.eventStartDay = '';
    this.eventStartMonth = '';
    this.eventStartYear = '';
    this.eventEndDay = '';
    this.eventEndMonth = '';
    this.eventEndYear = '';
  }

  addNewSignificantEvent(event: AddEventLifeLine) {
    const newSignificantEvent = {
      patient_id: this.patientId,
      ...event,
    };
    this.patientService.createLifeLineEvent(newSignificantEvent).subscribe({
      next: (response) => {
        this.toggleNewSignificantEventPanel();
        this.fetchSignificantEvents();
      },
      error: (err) => {
        console.error('Errore nel creare il nuovo evento significativo', err);
        this.toggleNewSignificantEventPanel();
        this.errorPanelService.showErrorSnackBar(
          'Errore nel creare il nuovo evento significativo'
        );
      },
    });
  }

  onModifyButtonClick() {
    this.hasModifyButtonBeenClicked = !this.hasModifyButtonBeenClicked;
    this.editOrDeleteString =
      this.editOrDeleteString === 'edit' ? 'delete' : 'edit';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobile = window.innerWidth < 768;
  }
}
